
// src/components/profiles/Member/components/posts/moment/MomentMyGrid.tsx
"use client";

import React, { useEffect, useCallback, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useRouter } from "next/navigation";

import type { AppDispatch } from "../../../../../../redux/storeTypes";
import { fetchMyMoments } from "../../../../../../redux/ugc/momentSlice";
import {
  selectMyMoments,
  selectMyMomentsLoading,
  selectMyMomentsHasMore,
} from "../../../../../../redux/selectors/ugc/momentSelectors";

import { Loader } from "../../../../../common";
import MomentGridItem from "./MomentGridItem";
import MomentEmptyState from "./MomentEmptyState";

import "./MomentMyGrid.css";

const MomentMyGrid: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const router = useRouter();
  const moments = useSelector(selectMyMoments);
  const loading = useSelector(selectMyMomentsLoading);
  const hasMore = useSelector(selectMyMomentsHasMore);
  const sentinelRef = useRef<HTMLDivElement | null>(null);

  // Initial fetch (page=1)
  useEffect(() => {
    dispatch(fetchMyMoments({ page: 1 }));
  }, [dispatch]);

  const handleCreate = useCallback(() => {
    router.push("/moments/create");
  }, [router]);

  // Load next page (guarded)
  const loadNext = useCallback(() => {
    if (loading) return;     // prevent double calls
    if (!hasMore) return;    // no more pages
    dispatch(fetchMyMoments());
  }, [dispatch, loading, hasMore]);

  // Infinite scroll observer
  useEffect(() => {
    const el = sentinelRef.current;
    if (!el) return;

    const io = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (!first?.isIntersecting) return;
        loadNext();
      },
      {
        root: null,              // viewport
        rootMargin: "300px 0px", // prefetch earlier
        threshold: 0,
      }
    );

    io.observe(el);
    return () => io.disconnect();
  }, [loadNext]);

  // Initial loading (only when list is empty)
  if (loading && moments.length === 0) {
    return (
      <div className="moment-my-grid__loading">
        <Loader variant="dots" size="md" color="glory" />
      </div>
    );
  }

  // Empty state
  if (!loading && moments.length === 0) {
    return <MomentEmptyState onCreate={handleCreate} />;
  }

  return (
    <section className="moment-my-grid" aria-label="My Moments">
      <div className="moment-my-grid__list">
        {moments.map((moment) => (
          <MomentGridItem key={moment.slug} moment={moment} />
        ))}
      </div>

      {/* Sentinel for infinite scroll */}
      <div ref={sentinelRef} className="moment-my-grid__sentinel" />

      {/* Bottom loader (when fetching next page) */}
      {loading && moments.length > 0 && (
        <div className="moment-my-grid__more">
          <Loader variant="dots" size="md" color="glory" />
        </div>
      )}

      {/* Optional: end hint */}
      {!hasMore && moments.length > 0 && (
        <div className="moment-my-grid__endHint">
          You’re all caught up on Moments ✨
        </div>
      )}
    </section>
  );
};

export default MomentMyGrid;